import type { AwardPeriod } from "./awards-types";

export function getAwardPeriods(now = new Date()): AwardPeriod[] {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const periods: AwardPeriod[] = [
    { label: "All time", key: "all-time" },
  ];

  for (let y = currentYear; y >= currentYear - 2; y--) {
    periods.push({ label: String(y), key: `year-${y}`, isoYear: y });
  }

  for (let i = 0; i < 6; i++) {
    let m = currentMonth - i;
    let y = currentYear;
    if (m <= 0) { m += 12; y--; }
    const monthName = new Date(y, m - 1, 1).toLocaleDateString("en-GB", {
      month: "short",
      year: "numeric",
    });
    periods.push({
      label: monthName,
      key: `month-${y}-${String(m).padStart(2, "0")}`,
      isoYear: y,
      isoMonth: m,
    });
  }

  return periods;
}

/**
 * Minimum number of scored days (non-null scores for the award's field) a
 * location needs before daily data can rank it (architecture review F8).
 *
 * The owner set 5 for weekly-period awards and 20 for season and all-time
 * awards. The published periods are calendar months, calendar years and all
 * time, so:
 * - SHORT (5): any period of about a month or less, including a future
 *   weekly period. A month in which a resort opened late still qualifies on
 *   a working week of evidence.
 * - LONG (20): calendar years (the season-length periods) and all time.
 * The reported `dataDays` is always this scored count, never a row count.
 */
export const AWARD_MIN_SCORED_DAYS_SHORT_PERIOD = 5;
export const AWARD_MIN_SCORED_DAYS_LONG_PERIOD = 20;
const SHORT_PERIOD_MAX_DAYS = 31;

export function minScoredDaysForPeriod(period: AwardPeriod): number {
  if (period.isoMonth) return AWARD_MIN_SCORED_DAYS_SHORT_PERIOD;
  if (period.startDate && period.endDate) {
    const days = (Date.parse(`${period.endDate}T00:00:00Z`) - Date.parse(`${period.startDate}T00:00:00Z`)) / 86_400_000 + 1;
    if (Number.isFinite(days) && days <= SHORT_PERIOD_MAX_DAYS) return AWARD_MIN_SCORED_DAYS_SHORT_PERIOD;
  }
  return AWARD_MIN_SCORED_DAYS_LONG_PERIOD;
}
